// src/SideWinder.Web/src/composables/useStrikeAnchorHealth.ts
//
// Task 11 of the 2026-09-16 gold/GGE strike-anchor arc (PR D, shadow-mode
// observability layer — spec docs/superpowers/specs/2026-09-16-gold-gge-
// strike-anchor-design.md). Drives StrikeAnchorHealthTile.vue's persistent
// per-fund freshness display by polling GET /api/system/strike-anchor-health
// (StrikeAnchorHealthEndpoint.cs), which itself reads the Redis key families
// Tasks 3/7 (frozen anchor), 8/9 (shadow verdict), and 10 (clean_session_count)
// already write.
//
// Fetch/poll pattern deliberately mirrors useStrikeAnchor.ts (eager fetch on
// call, not gated behind onMounted, so a bare composable-level unit test
// with no component instance still triggers the load; onScopeDispose only
// registers cleanup inside an active effect scope).

import { ref, getCurrentScope, onScopeDispose, type Ref } from 'vue'
import { api, type StrikeAnchorFundHealth } from '../api/client'

// Matches useStrikeAnchor.ts's POLL_INTERVAL_MS — the underlying Redis keys
// this endpoint reads are themselves only written once per strike (anchor),
// once per day at 13:00 UK (clean_session_count), and once per fund shortly
// after strike (verdict), so a 5-minute client poll is well inside every
// producer's own cadence.
const POLL_INTERVAL_MS = 300_000

export interface UseStrikeAnchorHealthResult {
  funds: Ref<StrikeAnchorFundHealth[]>
  available: Ref<boolean>
}

export function useStrikeAnchorHealth(): UseStrikeAnchorHealthResult {
  const funds = ref<StrikeAnchorFundHealth[]>([])
  const available = ref(false)

  async function load(): Promise<void> {
    try {
      const resp = await api.strikeAnchorHealth()
      funds.value = resp.funds
      available.value = true
    } catch {
      // Network error / api unreachable — keep the last known rows rather
      // than flashing the tile to empty (same idiom as useStrikeAnchor.ts's
      // load()).
    }
  }

  void load()
  const timer = setInterval(() => { void load() }, POLL_INTERVAL_MS)
  if (getCurrentScope()) onScopeDispose(() => clearInterval(timer))

  return { funds, available }
}
